import * as React from 'react';
import { FixMeLater } from '../../utils';
import { Styler, StylerBaseProps } from '../Styler';
import { ResponsiveStyleValue, useComponentDefaultProps } from '../../theming';
import { STATIC_STYLES, useCssVariables } from './Image.styles';
import { useEventCallback } from '../../hooks';
import { ImagePlaceholderIcon } from './ImagePlaceholderIcon';
import { ImageBase } from '../ImageBase/ImageBase';
import { ImageSet } from '../../types';
import { Picture } from '../Picture';

export interface ImageProps extends StylerBaseProps {
    /**
     * The image src.
     * Either a single image src or an ImageSet object, which can specify a unique image src per breakpoint.
     */
    src?: string | ImageSet | null;

    /**
     * The `srcset` attribute of the image.
     */
    srcset?: string;

    /**
     * The image alt text. This will also be used as the title for the placeholder if image was not loaded.
     */
    alt?: string;

    /**
     * Display a placeholder while image is loading or when the image fails to load.
     */
    withPlaceholder?: boolean;

    /**
     * Custom placeholder content.
     */
    placeholder?: React.ReactNode;

    /**
     * The `object-fit` css property of the image
     * @default cover
     */
    fit?: React.CSSProperties['objectFit'];

    /**
     * The `width` css property of the image
     * @default auto
     */
    width?: ResponsiveStyleValue<number | React.CSSProperties['width']>;

    /**
     * The `height` css property of the image
     * @default 100%
     */
    height?: ResponsiveStyleValue<number | React.CSSProperties['width']>;

    /**
     * Applies a border-radius to all corners of the image.
     * If a number, the value will be in pixels.
     * If a string, the value can be any valid css `border-radius` property.
     */
    radius?: number | React.CSSProperties['borderRadius'];

    /**
     * Content to be overlaid on the image.
     */
    children?: React.ReactNode;

    /**
     * Applies a background gradient mask to improve legibility of overlaid text.
     */
    backgroundMask?: React.CSSProperties['background'];

    /**
     * Props that will be spread to `img` element.
     */
    ImageElementProps?: React.ComponentPropsWithoutRef<'img'>;

    /**
     * A reference to the `img` element.
     */
    imageRef?: React.ForwardedRef<HTMLImageElement>;

    /**
     * Set to `true` if the image is the child of a `picture` element.
     */
    isPicture?: boolean;

    /**
     * Callback fired when the image has loaded.
     */
    onImageLoaded?: () => void;

    /**
     * Callback fired if the image fails to load.
     */
    onImageError?: () => void;

    /**
     * If true the picture will not be lazy loaded.
     */
    disableLazy?: boolean;
}

const defaultProps: Partial<ImageProps> = {
    fit: 'cover',
    height: 'auto',
    width: '100%',
    radius: 0,
};

export const Image: React.ForwardRefExoticComponent<ImageProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Image(props: ImageProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            fit,
            radius,
            width,
            height,
            src,
            srcset,
            alt,
            imageRef,
            ImageElementProps,
            onImageLoaded,
            onImageError,
            withPlaceholder,
            placeholder,
            backgroundMask,
            isPicture,
            disableLazy = false,
            ...other
        } = useComponentDefaultProps('Image', defaultProps, props);

        const [imageLoaded, setImageLoaded] = React.useState(false);

        const handleImageLoaded = useEventCallback(() => {
            setImageLoaded(true);
            if (onImageLoaded) {
                onImageLoaded();
            }
        });

        const getImageComponent = () => {
            if (!src) {
                return null;
            }
            const commonProps = {
                ref: imageRef,
                alt,
                'data-loaded': imageLoaded || undefined,
                onImageLoaded: handleImageLoaded,
                onImageError,
                disableLazy,
                ...ImageElementProps,
            };
            if (typeof src === 'object') {
                // We have a set of responsive image sources
                return <Picture imageSet={src as ImageSet} {...commonProps} />;
            }
            // We have a single image source
            return <ImageBase src={src} srcset={srcset} {...commonProps} />;
        };

        const cssVariables = useCssVariables(fit, radius, backgroundMask);

        return (
            <Styler
                ref={ref}
                style={cssVariables}
                baseStyles={STATIC_STYLES}
                data-image-root={true}
                w={width}
                h={height}
                {...other}
            >
                {withPlaceholder && (
                    <div data-placeholder={true} title={alt}>
                        {placeholder || <ImagePlaceholderIcon size="2xl" />}
                    </div>
                )}
                {getImageComponent()}
                {backgroundMask && <div data-mask={true} />}
                {children}
            </Styler>
        );
    }
);
